// Astrea Ops - split the old combined venue boxes into separate fields (v7.65, 9 Oct 2026).
//
// Up to v7.64 the Event Form held each venue as one free-text box ("#1 Venue Name / Address /
// Point of Contact"). From v7.65 the main venue (#1) has its own name, address and main
// telephone fields on Event Info, and venues #2 and #3 add a primary POC (name, title, phone,
// email) on Site Visit / Venue. Text already in the old boxes is split once, when the form is
// opened, and every field filled this way is flagged for review ("Split"). When the splitter is
// unsure it leaves a field empty rather than guessing. The old text (venue1 / venue2 / venue3) is
// never deleted; it is shown as "Original entry" until the split fields are confirmed.
//
// Spec: claude/venue-fields-spec.md in the project.

export const VENUE_FIELD_KEYS = {
  1: ['venue1Name', 'venue1Address', 'venue1Phone'],
  2: ['venue2Name', 'venue2Address', 'venue2Phone', 'venue2PocName', 'venue2PocTitle', 'venue2PocPhone', 'venue2PocEmail'],
  3: ['venue3Name', 'venue3Address', 'venue3Phone', 'venue3PocName', 'venue3PocTitle', 'venue3PocPhone', 'venue3PocEmail'],
};

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}/;
// US numbers (optionally +1, optional extension), and international numbers written with a "+".
const PHONE_RE = /(?:\+1[\s.-]?)?(?:\(\d{3}\)|\b\d{3})[\s.-]?\d{3}[\s.-]?\d{4}\b(?:\s*(?:x|ext\.?|extension)\s*\d{1,6})?|\+[2-9]\d{0,2}(?:[\s.-]?\d){7,13}\b/i;
// Street number through ZIP: "221 North Rampart Blvd, Las Vegas, NV 89145"
const ADDR_AT = /\d{1,6}[A-Za-z]?\s+[A-Za-z0-9.'#&/ ]{2,70}?(?:,\s*(?:Suite|Ste\.?|Unit|Floor|Fl\.?|Building|Bldg\.?|#)\s*[A-Za-z0-9-]+)?,?\s+[A-Za-z .'-]{2,40},\s*[A-Z]{2}\.?\s+\d{5}(?:-\d{4})?\b/y;
const POC_RE = /\b(?:primary\s+)?(?:POC|point\s+of\s+contact)\b\s*(?:is|-|:)?\s*/i;
const EMERGENCY_WORDS = /dispatch|security|emergency|control\s*room|command|police|fire|ems\b|24\s*\/\s*7|after[\s-]*hours/i;
// In a POC's own text "security" is usually their title, so only these mark a separate line.
const EMERGENCY_STRICT = /dispatch|emergency|control\s*room|command\s*(?:center|centre)|24\s*\/\s*7|after[\s-]*hours/i;
const PLACEHOLDER = /^(?:tbd|tbc|tba|n\/?a|none|unknown|pending|to be (?:confirmed|determined))\.?$/i;
const MAIN_WORDS = /^(?:main|phone|tel|telephone|switchboard|front\s*desk|hotel|venue|reservations?|general|office|ph|t)\b/i;

const clean = (s) => String(s || '')
  .replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, '"')
  .replace(/[\u2013\u2014]/g, ' - ').replace(/\u2026/g, '...').replace(/[\u00A0\u200B\u2007\u202F]/g, ' ');

// Tidy separators left behind after pieces are cut out of a string.
const tidy = (s) => String(s || '')
  .replace(/\s+/g, ' ')
  .replace(/(?:\s*[-,;:|/]\s*){2,}/g, ' - ')
  .replace(/^[\s\-,;:|/.]+|[\s\-,;:|/]+$/g, '')
  .replace(/\(\s*\)/g, '')
  .trim();

const hasLetters = (s) => /[A-Za-z]{2}/.test(s || '');

// A segment is the text between " - " separators (or line breaks).
function segmentsOf(text) {
  return clean(text)
    .split(/\r?\n|\s+-\s+|\s+\u00B7\s+|;\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// Strip labels a person might have typed in front of the name ("Venue #1:", "Name -").
function stripNameLabel(s) {
  return String(s || '')
    .replace(/^\s*(?:#\s*\d\s*[:.)-]?\s*)?(?:main\s+)?venue(?:\s*#?\s*\d)?(?:\s+name)?\s*[:-]\s*/i, '')
    .replace(/^\s*(?:name|property)\s*[:-]\s*/i, '')
    .trim();
}

// The rightmost street-number start that reads as a full address (so a number in the name,
// such as "The 1847 Hotel", is not taken as the street number).
function findAddress(seg) {
  let best = null;
  const re = /\b\d/g;
  let m;
  while ((m = re.exec(seg))) {
    ADDR_AT.lastIndex = m.index;
    const a = ADDR_AT.exec(seg);
    if (a) {
      const street = a[0].split(',')[0];
      if (/[A-Za-z]{2}/.test(street.replace(/^\d+[A-Za-z]?\s+/, ''))) best = { start: m.index, text: a[0].trim() };
    }
  }
  return best;
}

// Phones with the words just before each one ("Security dispatch direct line: 702-...").
function phonesIn(s) {
  const out = [];
  const str = String(s || '');
  const re = new RegExp(PHONE_RE.source, 'gi');
  let m;
  let last = 0;
  while ((m = re.exec(str))) {
    const seg = str.slice(last, m.index).split(/\s+-\s+|[;|,\n)]/).pop();
    out.push({ phone: m[0].trim(), label: tidy(seg.replace(/[:=#]\s*$/, '')) });
    last = m.index + m[0].length;
  }
  return out;
}
const EMAIL_ONE = new RegExp(EMAIL_RE.source);
const stripEmails = (s) => String(s || '').replace(new RegExp(EMAIL_RE.source, 'g'), ' ');
const stripPhones = (s) => String(s || '').replace(new RegExp(PHONE_RE.source, 'gi'), ' ');

const looksLikePerson = (s) => /^[A-Za-z][A-Za-z.'\- ]{1,60}$/.test(s) && s.split(/\s+/).length <= 5 && s.split(/\s+/).length >= 1 && !/\b(?:hotel|resort|center|centre|venue|security|director|manager|dispatch|line|main|phone)\b/i.test(s);

function parsePoc(post) {
  const poc = { name: '', title: '', phone: '', email: '' };
  const extra = [];
  let s = clean(post);
  const em = s.match(EMAIL_ONE);
  if (em) { poc.email = em[0]; s = stripEmails(s); }
  for (const p of phonesIn(s)) {
    if (p.label && EMERGENCY_STRICT.test(p.label)) extra.push({ kind: 'emergency', phone: p.phone, label: p.label });
    else if (!poc.phone) poc.phone = p.phone;
    else extra.push({ kind: 'phone', phone: p.phone, label: p.label });
    s = s.replace(p.phone, ' ');
  }
  // drop the labels that went with the phone and email
  s = s.replace(/\b(?:e-?mail|cell|mobile|mob|direct|office|tel|phone|ph|c|m|o|e)\s*[:.]\s*(?=\s|-|,|$)/gi, ' ');
  const segs = segmentsOf(s).map(tidy).filter(hasLetters);
  if (segs.length) {
    const first = segs[0];
    let name = '';
    let title = '';
    if (first.includes(',')) {
      name = first.slice(0, first.indexOf(',')).trim();
      title = first.slice(first.indexOf(',') + 1).trim();
    } else if (/\(([^)]+)\)/.test(first)) {
      name = first.replace(/\(([^)]+)\)/, '').trim();
      title = first.match(/\(([^)]+)\)/)[1].trim();
    } else {
      name = first;
      if (segs[1] && segs[1].length <= 80) title = segs[1];
    }
    name = tidy(name);
    title = tidy(title);
    if (looksLikePerson(name)) poc.name = name;
    if (title && hasLetters(title) && title.length <= 80 && !EMAIL_ONE.test(title)) poc.title = title;
  }
  return { poc, extra };
}

// Split one old combined box. Returns what it is sure of; anything else stays empty.
export function splitVenueText(text) {
  const out = { name: '', address: '', phone: '', poc: { name: '', title: '', phone: '', email: '' }, emergency: [], email: '' };
  const raw = clean(text).trim();
  if (!raw) return out;
  // Venue part and POC part
  let pre = raw;
  let post = '';
  const pm = raw.match(POC_RE);
  if (pm) { pre = raw.slice(0, pm.index); post = raw.slice(pm.index + pm[0].length); }
  // Address first (so a ZIP is never read as part of a phone number)
  const segs = segmentsOf(pre);
  let nameCand = segs[0] || '';
  for (let i = 0; i < segs.length; i++) {
    const a = findAddress(segs[i]);
    if (!a) continue;
    out.address = tidy(a.text);
    if (i === 0) nameCand = segs[0].slice(0, a.start);
    segs[i] = segs[i].slice(0, a.start) + ' ' + segs[i].slice(a.start + a.text.length);
    break;
  }
  // Phones and email in the venue part
  const rest = segs.join(' - ');
  const vem = rest.match(EMAIL_ONE);
  if (vem) out.email = vem[0];
  const nameTidy = tidy(nameCand);
  for (const p of phonesIn(rest)) {
    if (p.label && EMERGENCY_WORDS.test(p.label)) out.emergency.push({ phone: p.phone, label: p.label });
    else if (!out.phone && (!p.label || MAIN_WORDS.test(p.label) || p.label === nameTidy)) out.phone = p.phone;
  }
  // Name: before the address or the first " - "; never a phone, email or label
  const name = tidy(stripPhones(stripEmails(stripNameLabel(nameCand))));
  if (name && hasLetters(name) && name.length <= 120 && !PLACEHOLDER.test(name) && !EMERGENCY_WORDS.test(name.replace(/\(.*?\)/g, ''))) out.name = name;
  if (post) {
    const r = parsePoc(post);
    out.poc = r.poc;
    r.extra.filter((x) => x.kind === 'emergency').forEach((x) => out.emergency.push({ phone: x.phone, label: x.label }));
  }
  return out;
}

// Same property? Names compared without "the", brackets and punctuation; addresses by street
// number and street name.
const normName = (s) => clean(s).toLowerCase().replace(/\(.*?\)/g, ' ').replace(/[^a-z0-9]+/g, ' ').replace(/^\s*the\s+/, '').replace(/\s+/g, ' ').trim();
const normAddr = (s) => {
  const m = clean(s).toLowerCase().match(/(\d{1,6})\s+([a-z0-9 .]+?)(?:,|$)/);
  return m ? m[1] + ' ' + m[2].replace(/\b(?:north|south|east|west|n|s|e|w)\b\.?/g, '').replace(/[^a-z0-9]+/g, ' ').trim().split(' ')[0] : '';
};
export function sameVenue(a, b) {
  if (!a || !b) return false;
  const na = normName(a.name || '');
  const nb = normName(b.name || '');
  if (na && nb && (na === nb || (na.length >= 8 && nb.includes(na)) || (nb.length >= 8 && na.includes(nb)))) return true;
  const aa = normAddr(a.address || '');
  const ab = normAddr(b.address || '');
  if (aa && ab && aa === ab) return true;
  // #2 text with no name of its own that mentions #1 by name
  if (!nb && na.length >= 8 && normName(b.text || '').includes(na)) return true;
  return false;
}

const filled = (v) => String(v ?? '').trim() !== '';
const pocText = (p) => [[p.name, p.title].filter(Boolean).join(', '), p.phone, p.email].filter(Boolean).join(' - ');
// Which venue #1 contact field a POC belongs in, by their title.
function contactFieldFor(title) {
  if (/security|safety|loss\s*prevention|protection/i.test(title || '')) return 'directorSecurity';
  if (/\bCSM\b|convention|conference|event|catering|services|sales|meeting/i.test(title || '')) return 'hotelVenueCSM';
  return '';
}
const FIELD_LABELS = {
  directorSecurity: 'Venue Director of Security',
  hotelVenueCSM: 'Hotel / Venue CSM',
  venueEmergencyNumber: 'Venue Emergency Number',
  venue1Phone: 'Main Venue Main Telephone',
  venue1Address: 'Main Venue Address',
};

// Apply the one-time split to a form. Fields are only ever filled when empty, each one gets a
// review entry {before:"", after, at, src:"split", venue:n}, and form.venueSplit records that it
// ran ({done, at, notes:{n:[...]}, dup:{n:true}}). Returns {ef, changed}.
export function applyVenueSplit(ef, at) {
  const form = ef || {};
  if (form.venueSplit && form.venueSplit.done) return { ef: form, changed: [] };
  const olds = [1, 2, 3].map((n) => String(form['venue' + n] || '').trim());
  if (!olds.some(Boolean)) return { ef: form, changed: [] };
  const out = { ...form };
  const rev = { ...(form.aiReview || {}) };
  const changed = [];
  const notes = { 1: [], 2: [], 3: [] };
  const dup = {};
  const put = (k, v, n) => {
    if (!filled(v) || filled(out[k])) return false;
    out[k] = v;
    rev[k] = { before: '', after: v, at, src: 'split', venue: n };
    changed.push(k);
    return true;
  };
  // Venue #1's contact and emergency lines go to the page 1 fields, only when those are empty.
  const placeMainExtras = (p, n) => {
    if (p.poc && (p.poc.name || p.poc.phone || p.poc.email)) {
      const who = p.poc.name || 'The point of contact';
      const k = contactFieldFor(p.poc.title);
      if (!k) notes[n].push(who + "'s details stay in the original entry (no title to tell whether they belong in Venue Director of Security or Hotel / Venue CSM).");
      else if (filled(out[k])) notes[n].push(who + "'s details stay in the original entry, because " + FIELD_LABELS[k] + ' already has a value.');
      else { put(k, pocText(p.poc), n); notes[n].push(who + "'s details went to " + FIELD_LABELS[k] + '.'); }
    }
    p.emergency.forEach((e) => {
      const lab = e.label ? e.label.charAt(0).toUpperCase() + e.label.slice(1) : 'Emergency line';
      const v = e.phone + (e.label ? ' (' + e.label.toLowerCase() + ')' : '');
      if (put('venueEmergencyNumber', v, n)) notes[n].push(lab + ' ' + e.phone + ' went to Venue Emergency Number.');
      else notes[n].push(lab + ' ' + e.phone + ' stays in the original entry (Venue Emergency Number already has a value).');
    });
  };
  // #1
  let main = { name: String(form.venue1Name || '').trim(), address: String(form.venue1Address || '').trim() };
  if (olds[0] && !VENUE_FIELD_KEYS[1].some((k) => filled(form[k]))) {
    const p = splitVenueText(olds[0]);
    put('venue1Name', p.name, 1);
    put('venue1Address', p.address, 1);
    put('venue1Phone', p.phone, 1);
    if (!p.phone) notes[1].push('No main telephone found in the original entry - fill it in if known.');
    placeMainExtras(p, 1);
    main = { name: out.venue1Name || p.name, address: out.venue1Address || p.address };
  }
  // #2 and #3
  [2, 3].forEach((n) => {
    const txt = olds[n - 1];
    if (!txt || VENUE_FIELD_KEYS[n].some((k) => filled(form[k]))) return;
    const p = splitVenueText(txt);
    if (sameVenue(main, { name: p.name, address: p.address, text: txt })) {
      dup[n] = true;
      notes[n].push('Same venue as #1 - this entry describes the main venue again, so it was not split into a second venue.');
      if (put('venue1Address', p.address, 1)) notes[n].push('Its address went to the Main Venue Address.');
      if (put('venue1Phone', p.phone, 1)) notes[n].push('Its main telephone went to the Main Venue Main Telephone.');
      placeMainExtras(p, n);
      return;
    }
    put('venue' + n + 'Name', p.name, n);
    put('venue' + n + 'Address', p.address, n);
    put('venue' + n + 'Phone', p.phone, n);
    put('venue' + n + 'PocName', p.poc.name, n);
    put('venue' + n + 'PocTitle', p.poc.title, n);
    put('venue' + n + 'PocPhone', p.poc.phone, n);
    put('venue' + n + 'PocEmail', p.poc.email, n);
    p.emergency.forEach((e) => notes[n].push((e.label || 'Emergency line') + ' ' + e.phone + ' stays in the original entry.'));
  });
  Object.keys(notes).forEach((n) => { if (!notes[n].length) delete notes[n]; });
  out.aiReview = rev;
  out.venueSplit = { done: true, at, notes, dup };
  return { ef: out, changed };
}

// The venue name to show or match on: the new field, else the old combined text.
export const venueNameOf = (ef, n = 1) => String((ef && (ef['venue' + n + 'Name'] || ef['venue' + n])) || '').trim();
